import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../contexts/ThemeContext";
import { useDirection } from "../../../contexts/DirectionContext";
import { Icon } from "@iconify/react";

interface SearchInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onSearch?: (value: string) => void;
  placeholder?: string;
  className?: string;
  size?: "sm" | "md" | "lg";
}

const SearchInput: React.FC<SearchInputProps> = ({
  value = "",
  onChange,
  onSearch,
  placeholder,
  className = "",
  size = "md",
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { isRTL } = useDirection();
  const [query, setQuery] = useState(value);

  const handleQueryChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newQuery = e.target.value;
      setQuery(newQuery);
      onChange?.(newQuery);
    },
    [onChange]
  );

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && onSearch) {
        onSearch(query.trim());
      }
    },
    [query, onSearch]
  );

  const sizeClasses = {
    sm: "h-10",
    md: "h-12 sm:h-14",
    lg: "h-14 sm:h-16",
  };

  return (
    <div className={`${className} relative z-[1000]`}>
      <div
        className={`
          relative
          flex items-stretch
          ${sizeClasses[size]}
          ${theme.mode === "dark"
            ? "bg-primary-900/30 text-primary-300 border-primary-700"
            : "bg-white/10 text-text-inverse border-text-inverse/30"
          }
          border
          rounded-full
          transition-all duration-200
          group/search
          overflow-visible
        `}
      >
        <div className="flex items-center px-6">
          <Icon
            icon="ph:magnifying-glass-thin"
            className={`
              w-5 h-5
              ${theme.mode === "dark" ? "text-text-secondary" : "text-text-inverse/60"}
            `}
          />
        </div>
        <input
          type="text"
          value={query}
          onChange={handleQueryChange}
          onKeyPress={handleKeyPress}
          placeholder={placeholder || t("common.searchPlaceholder")}
          className={`
            flex-1
            pe-6 ps-0
            bg-transparent
            ${theme.mode === "dark" ? "text-text-primary" : "text-text-inverse"}
            placeholder-text-secondary/40
            text-sm sm:text-base
            font-light
            focus:outline-none
            min-w-0
          `}
          dir={isRTL ? "rtl" : "ltr"}
        />

        {onSearch && (
          <div className="flex items-center p-2">
            <button
              onClick={() => onSearch(query.trim())}
              aria-label={t("common.search")}
              className={`
                flex-shrink-0
                flex items-center justify-center
                w-8 h-8 sm:w-10 sm:h-10
                bg-primary-500
                hover:bg-primary-600
                active:bg-primary-700
                focus:outline-none
                text-white
                transition-all duration-200
                group
                rounded-full
              `}
            >
              <Icon
                icon="ph:magnifying-glass-thin"
                className="
                  w-4 h-4 sm:w-5 sm:h-5
                  transition-transform
                  group-hover:scale-110
                  group-focus:scale-110
                "
              />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchInput; 